import { useCallback, useEffect, useRef, useState } from 'react';
import { App } from 'antd';

import { api, readTotalCount } from '../api/client';
import { API_ITEMS_URL } from '../utils';
import { listCountKey } from './useLinkedCount';
import { queryWarningsKey, readCanonicalQuery, readQueryWarnings } from '../utils/queryWarnings';
import { elementChoicesParam } from '../utils/elementRefusal';
import { sortThenParams } from '../utils/sortLevels';
import { retryAfterMs as parseRetryAfterMs } from '../utils/retryAfter';
import { RATE_LIMITED_TEXT } from '../utils/feedMessages';

const PAGE_SIZE = 50;

// A 429 whose Retry-After names a wait this short is asked again ONCE, by
// itself, after the wait. A longer wait, a second refusal, or any other
// failure stops the feed until the person asks (`retry`).
export const AUTO_RETRY_MAX_MS = 30e3;

// The feed's one toast. antd replaces a message that carries the same key
// instead of stacking another beside it, so a feed that fails again (a retry,
// a refresh) never piles them up.
export const FEED_ERROR_KEY = 'items-feed-error';

export const FEED_ERROR_TEXT = 'Failed to load items';
// Said by the list's footer too (components/items/ListScrollLoader), which reads
// it from utils/feedMessages so it does not import this hook's network.
export { RATE_LIMITED_TEXT };

// A bag of extra request parameters as the one string that identifies it, so
// a caller re-rendering with an equal object never restarts the feed. Keys
// are sorted; an undefined value is no parameter.
export function extraParamsKey(extra) {
	if (!extra || typeof extra !== 'object') return undefined;
	const entries = Object.keys(extra).sort()
		.filter(name => extra[name] !== undefined)
		.map(name => [name, extra[name]]);
	return entries.length ? JSON.stringify(entries) : undefined;
}

/**
 * How long a refusal asks the client to wait, in ms (utils/retryAfter.js);
 * kept here for useMapItems.
 */
export const retryAfterMs = (error, now = Date.now()) => parseRetryAfterMs(error, { now });

// What one request for a page came to, besides a failure.
const LANDED = 'landed';
const STALE = 'stale';
// A 400 on a filtered list, handed to the caller's onQueryError.
const REFUSED = 'refused';

const waitOut = (ref, ms) => new Promise(resolve => {
	const timer = setTimeout(() => {
		ref.current = null;
		resolve(true);
	}, ms);
	ref.current = { timer, resolve };
});

const cancelWait = (ref) => {
	const pending = ref.current;
	if (!pending) return;
	ref.current = null;
	clearTimeout(pending.timer);
	pending.resolve(false);
};

/**
 * The collection items feed: server-paged (skip/limit + x-total-count) but
 * consumed as one growing list — the items page appends pages as the user
 * scrolls instead of swapping them.
 *
 * Card-type filtering, KQL (`query` → `q`), and sorting are query params so
 * the server does the work. Any change to them resets the feed to its first
 * page — and ONLY a change to them (or to `enabled`): the callbacks a caller
 * hands in are read through refs, so a caller re-rendering with a new
 * `onQueryError` (every query-string change did, through react-router's
 * `setSearchParams`) never restarts the list. (Cursor-based paging would
 * survive concurrent inserts better; the API only speaks skip/limit today.)
 *
 * A page that fails STOPS the feed. `loadError` says why —
 * `{ kind: 'rate_limited' | 'failed', page, retryAfterMs? }` — and `loadMore`
 * refuses until `retry()` asks for that same page again, so an auto-loader
 * that fires on its own (the page's IntersectionObserver) cannot turn one
 * refusal into a request per frame (2026-09-27: 1,081 requests in two
 * minutes, one toast each). A 429 that names a wait of AUTO_RETRY_MAX_MS or
 * less is retried once, by itself, after it. When the first page of a NEW
 * feed fails, the previous feed's rows are dropped rather than left under
 * the new question. `loadMore`, `retry` and `refresh` keep one identity for
 * the hook's lifetime; `landedPages` counts every page that lands, which is
 * what an auto-loader re-arms on.
 *
 * `queryWarnings` is what the server said the filter names that is gone —
 * the first page's `X-Query-Warnings` (utils/queryWarnings.js), `[]` when it
 * said nothing — and `queryWarningsOmitted` how many more it had no room
 * for. They belong to the filter: a new collection, card or query starts
 * without them; any other restart (a sort, the linked switch, a refresh)
 * keeps them on screen until its own first page answers, so the caution
 * never flickers.
 *
 * `canonicalQuery` is what the server read `query` as — its
 * `X-Query-Canonical` (deterministic filters F3): the names a schema reader
 * typed resolved to ids, a choice label to its value. It is tied to the query
 * it answered: null until that query's first page lands, and null whenever
 * the server sent none (a reader who may not read the schema, no `q`).
 * `onQueryError(message, body)` receives the refusal's whole body beside its
 * message, so a page can offer what `ambiguous_tag` and `ambiguous_element`
 * list. `elementChoices` is the person's answer to an `ambiguous_element`
 * (utils/elementRefusal.js): sent as the `elementChoices` parameter with
 * `q`, and a new answer restarts the feed — the server then reads the name
 * as the element picked, and says so in `X-Query-Canonical`.
 */
export default function useCollectionItems({
	collectionId,
	cardFilter,
	// The merged list (keepr-api § Merged lists): linked collections' records
	// join this collection's, each carrying `linkedFrom`.
	includeLinked = false,
	query,
	// The intake quarantine: 'pending' | 'accepted' | 'any', or absent for the
	// accepted list (the API's default leaves unreviewed submissions out).
	review,
	sortField,
	sortDirection,
	// "Then by" (KPR-1636): sort levels 2 and 3, `[{ field, direction }]`
	// (utils/sortLevels), sent as `sort_field_2` … Absent or [] sends nothing,
	// so a one-level request is exactly what it was.
	sortThen = null,
	onQueryError,
	// The person's picks for element names `query` could mean several of
	// (element ids step 3): `{ <choiceKey>: id | [ids] }`, or null.
	elementChoices = null,
	// False holds the first request until what it means is known — the items
	// page waits for the collection's links, so a first visit does not fetch
	// the plain list and then, a moment later, the merged one. The feed reads
	// as loading meanwhile.
	enabled = true,
	// Extra request parameters, sent unchanged on every page request beside
	// the feed's own (a related list's `related_item` / `related_list`). The
	// feed's own parameters win a clash. A change to the bag restarts the
	// feed from the first page, like any other change in what it means.
	extraParams = null,
}) {
	const { message } = App.useApp();
	// Read at the moment they are needed, never as dependencies: only what the
	// feed MEANS may give fetchPage a new identity.
	const messageRef = useRef(message);
	messageRef.current = message;
	const onQueryErrorRef = useRef(onQueryError);
	onQueryErrorRef.current = onQueryError;
	const enabledRef = useRef(enabled);
	enabledRef.current = enabled;
	// Read by the restart below to tell a new filter from a new sort.
	const collectionIdRef = useRef(collectionId);
	collectionIdRef.current = collectionId;
	const cardFilterRef = useRef(cardFilter);
	cardFilterRef.current = cardFilter;
	const queryRef = useRef(query);
	queryRef.current = query;
	// The picks as the one string the request carries: a re-render with an
	// equal object must not restart the feed.
	const extraKey = extraParamsKey(extraParams);
	const choicesParam = query ? elementChoicesParam(elementChoices) : undefined;
	// The levels as the one string the request carries (an equal list must
	// not restart the feed).
	const sortThenKey = JSON.stringify(sortThenParams(sortThen));

	const [items, setItems] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	// The question `totalCount` answers (useLinkedCount listCountKey): a
	// total is only ever read as the answer to the question it answered.
	const [totalKey, setTotalKey] = useState(null);
	const [loading, setLoading] = useState(true); // first page of a fresh feed
	const [loadingMore, setLoadingMore] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [loadError, setLoadError] = useState(null);
	const [queryReport, setQueryReport] = useState(() => readQueryWarnings(null));
	// `{ query, text }`: the canonical text, and the query it was the answer to.
	const [canonical, setCanonical] = useState(null);
	// The filter the warnings answer: collection, card and query. Nothing
	// else a feed restarts on (sort, review, linked) changes what they say.
	const warnedQuestionRef = useRef(null);
	// Counts every page that lands, a new feed's first page included. The
	// page's auto-loader re-arms on it: an IntersectionObserver reports only
	// CHANGES, so one that fired while a page was in flight (and was refused)
	// stays silent after that page lands unless it is re-armed — and a new
	// feed's first page can land with exactly as many items as the last. It
	// moves only once the feed is free to take the next page (see `settle`).
	const [landedPages, setLandedPages] = useState(0);
	// Generation guard: a stale response (from before a filter change) must
	// never splice into the new feed.
	const generationRef = useRef(0);
	// The generation whose first page `loading` is waiting for. Separate from
	// generationRef because a refresh() also moves that one: a refresh that
	// lands while the first page is still in flight used to strand `loading`
	// at true for good (the first fetch's finally no longer matched, and a
	// refresh never clears it) — a list that never stops loading.
	const loadingGenerationRef = useRef(0);
	// How many pages of THIS feed have landed; the next page is this index. A
	// page 0 that fails leaves it at 0, so the next request is page 0 again —
	// never page 1 spliced onto the previous feed's rows.
	const loadedPagesRef = useRef(0);
	const hasMoreRef = useRef(true);
	// A page of the current generation is in flight, or waiting out a
	// Retry-After. One at a time.
	const busyRef = useRef(false);
	// The index of the page that failed and is still owed; `loadMore` refuses
	// while it stands.
	const failedPageRef = useRef(null);
	// The automatic retry waiting out its Retry-After: { timer, resolve }.
	const retryWaitRef = useRef(null);
	// The person's Refresh (`reload`, KPR-1346): re-reads every page on
	// screen and swaps them in at once. `reloading` is true while it runs;
	// `reloadError` is `{ kind }` when the last one failed (the rows, count
	// and paging are untouched). The promise in flight, so a second press
	// joins it rather than asking again.
	const [reloading, setReloading] = useState(false);
	const [reloadError, setReloadError] = useState(null);
	const reloadRef = useRef(null);
	const itemsRef = useRef(items);
	itemsRef.current = items;

	// The request for one page of the feed, as it means now.
	const paramsFor = useCallback((pageIndex) => {
		const params = {
			...(extraKey ? Object.fromEntries(JSON.parse(extraKey)) : {}),
			collection_id: collectionId,
			limit: PAGE_SIZE,
			skip: pageIndex * PAGE_SIZE,
			sort_field: sortField,
			sort_direction: sortDirection,
			...JSON.parse(sortThenKey),
		};
		if (cardFilter) params.card_id = cardFilter;
		if (includeLinked) params.includeLinked = 1;
		// KQL filter text (see utils/kql). Blank is simply no filter; the
		// server ANDs it with card_id and with the caller's own scope.
		if (query) params.q = query;
		if (choicesParam) params.elementChoices = choicesParam;
		if (review) params.review = review;
		return params;
	}, [collectionId, cardFilter, includeLinked, query, choicesParam, review, sortField, sortDirection, sortThenKey, extraKey]);
	const paramsForRef = useRef(paramsFor);
	paramsForRef.current = paramsFor;

	const fetchPage = useCallback((pageIndex, generation) => {
		const params = paramsFor(pageIndex);

		return api.get(API_ITEMS_URL, { params })
			.then(({ data, headers }) => {
				if (generation !== generationRef.current) return STALE;
				const pageItems = data || [];
				if (pageIndex === 0) {
					// Same words as before, same object: a restart that says
					// nothing new re-renders nothing.
					const report = readQueryWarnings(headers);
					setQueryReport(prev => (queryWarningsKey(prev) === queryWarningsKey(report) ? prev : report));
					const text = readCanonicalQuery(headers);
					setCanonical(prev => (prev && prev.query === query && prev.text === text ? prev : { query, text }));
				}
				setItems(prev => {
					const next = pageIndex === 0 ? pageItems : [...prev, ...pageItems];
					const total = readTotalCount(headers, next.length);
					setTotalCount(total);
					if (pageIndex === 0) setTotalKey(listCountKey({ collectionId, cardFilter, query, review, includeLinked, extra: extraKey }));
					const more = pageItems.length === PAGE_SIZE && next.length < total;
					hasMoreRef.current = more;
					setHasMore(more);
					return next;
				});
				loadedPagesRef.current = pageIndex + 1;
				return LANDED;
			})
			.catch(error => {
				if (generation !== generationRef.current) return STALE;
				// The only 400 a filtered list gets is a malformed q — a stale
				// link, or an API that predates the query engine. The caller
				// decides what to do with the query; it must never leave a
				// page that can only ever fail.
				if (query && error?.response?.status === 400 && onQueryErrorRef.current) {
					onQueryErrorRef.current(error.response?.data?.message, error.response?.data || {});
					return REFUSED;
				}
				return { error };
			});
	}, [paramsFor, collectionId, cardFilter, includeLinked, query, review, extraKey]);

	// One page, start to finish: asked for, and — when a 429 names a short
	// wait — asked for ONCE more after it. Resolves once the page has landed
	// (true), failed for good or gone stale (false); never rejects.
	//
	// `newFeed` marks the first page of a feed that means something new (the
	// reset below, not a refresh). The rows still on screen answer the OLD
	// question, so the moment that page fails they go: a worklist drawn over
	// them would offer the previous feed's rows as its pass, and Save would
	// write them.
	const loadPage = useCallback((pageIndex, generation, { newFeed = false } = {}) => {
		const attempt = (mayRetry) => fetchPage(pageIndex, generation).then(result => {
			if (result === LANDED) return true;
			if (result === STALE) return false;
			if (newFeed && pageIndex === 0) {
				setItems([]);
				setTotalCount(0);
				setTotalKey(null);
			}
			if (result === REFUSED) {
				// The caller is dropping (or blocking) the query, or asking
				// which record it meant (an ambiguous tag keeps the filter);
				// nothing pages on its own meanwhile, and the rows of the
				// previous question are gone with the rest (the F3 review's
				// F3). Its own reset clears this.
				failedPageRef.current = pageIndex;
				return false;
			}
			const { error } = result;
			const rateLimited = error?.response?.status === 429;
			const wait = rateLimited ? retryAfterMs(error) : null;
			if (mayRetry && wait !== null && wait <= AUTO_RETRY_MAX_MS) {
				return waitOut(retryWaitRef, wait).then(go => (
					go && generation === generationRef.current ? attempt(false) : false
				));
			}
			failedPageRef.current = pageIndex;
			setLoadError({
				kind: rateLimited ? 'rate_limited' : 'failed',
				page: pageIndex,
				...(wait !== null ? { retryAfterMs: wait } : {}),
			});
			// A first page that fails leaves nothing on screen to explain
			// itself, so it is said out loud as well; a later page's failure
			// is the inline row at the list's end alone.
			if (pageIndex === 0) {
				messageRef.current.error({ content: rateLimited ? RATE_LIMITED_TEXT : FEED_ERROR_TEXT, key: FEED_ERROR_KEY });
			}
			return false;
		});
		return attempt(true);
	}, [fetchPage]);
	const loadPageRef = useRef(loadPage);
	loadPageRef.current = loadPage;

	// Everything a new feed (or a refresh) starts without.
	const startOver = useCallback(() => {
		cancelWait(retryWaitRef);
		loadedPagesRef.current = 0;
		failedPageRef.current = null;
		hasMoreRef.current = true;
		busyRef.current = false;
		setHasMore(true);
		setLoadError(null);
		// A page in flight for the old feed must not strand the spinner: its
		// own `finally` belongs to a generation that is gone.
		setLoadingMore(false);
		// A Refresh of the old feed is over: its answer is stale.
		reloadRef.current = null;
		setReloading(false);
		setReloadError(null);
	}, []);

	// A page of `generation` has finished: the feed is free again, and — only
	// now, so an auto-loader re-armed by it is not refused as busy — a page
	// that landed is counted.
	const settle = useCallback((generation, landed) => {
		if (generation !== generationRef.current) return false;
		busyRef.current = false;
		if (landed) setLandedPages(count => count + 1);
		return true;
	}, []);

	// Any change in what the feed means starts it over.
	useEffect(() => {
		const generation = ++generationRef.current;
		loadingGenerationRef.current = generation;
		startOver();
		setLoading(true);
		// The warnings answered the previous filter; a restart that asks the
		// same filter (a sort, the linked switch) keeps them until it answers.
		const question = JSON.stringify([collectionIdRef.current, cardFilterRef.current || null, queryRef.current || '']);
		if (warnedQuestionRef.current !== null && warnedQuestionRef.current !== question) {
			setQueryReport(prev => (prev.warnings.length || prev.omitted ? readQueryWarnings(null) : prev));
		}
		warnedQuestionRef.current = question;
		if (!enabled) return;
		busyRef.current = true;
		loadPage(0, generation, { newFeed: true }).then(landed => {
			settle(generation, landed);
			if (generation === loadingGenerationRef.current) setLoading(false);
		});
	}, [loadPage, enabled, startOver, settle]);

	// Unmounted, every answer still on its way is stale: none may start a
	// Retry-After wait (and then a request), toast, or touch state.
	useEffect(() => () => {
		generationRef.current += 1;
		cancelWait(retryWaitRef);
	}, []);

	const loadMore = useCallback(() => {
		if (!enabledRef.current || busyRef.current || failedPageRef.current !== null) return;
		if (!hasMoreRef.current || loadedPagesRef.current === 0) return;
		const generation = generationRef.current;
		busyRef.current = true;
		setLoadingMore(true);
		loadPageRef.current(loadedPagesRef.current, generation).then(landed => {
			if (settle(generation, landed)) setLoadingMore(false);
		});
	}, [settle]);

	// Ask again for the page that failed — the same index, so a failed first
	// page is asked for as the first page, and a failed page 3 as page 3.
	const retry = useCallback(() => {
		const page = failedPageRef.current;
		if (page === null || busyRef.current || !enabledRef.current) return;
		const generation = generationRef.current;
		failedPageRef.current = null;
		busyRef.current = true;
		setLoadError(null);
		// Nothing of this feed on screen yet: it reads as loading again, not
		// as an empty list.
		const fresh = loadedPagesRef.current === 0;
		if (fresh) {
			loadingGenerationRef.current = generation;
			setLoading(true);
		} else {
			setLoadingMore(true);
		}
		loadPageRef.current(page, generation).then(landed => {
			if (fresh && generation === loadingGenerationRef.current) setLoading(false);
			if (settle(generation, landed) && !fresh) setLoadingMore(false);
		});
	}, [settle]);

	const retryRef = useRef(retry);
	retryRef.current = retry;

	const refresh = useCallback(() => {
		const generation = ++generationRef.current;
		startOver();
		if (!enabledRef.current) return;
		busyRef.current = true;
		loadPageRef.current(0, generation).then(landed => settle(generation, landed));
	}, [startOver, settle]);

	// The person's Refresh (KPR-1346): the same question asked again for
	// every page they have on screen — pages 0..n-1, one after another — so
	// a list scrolled to page 3 comes back as three pages, not one. The rows
	// on screen stay until every page has landed, then the new ones replace
	// them in one go (an item changed elsewhere shows its new values, one
	// that left the filter or was deleted goes, one that now matches joins,
	// and the count is the server's new total). A page that comes back short
	// ends the read early: the list got shorter.
	//
	// Nothing about the feed's meaning changes, so nothing restarts: the
	// generation moves only so a page `loadMore` had in flight cannot splice
	// onto the new rows. A failure changes nothing on screen — rows, count,
	// paging and any earlier page failure stay — and says so in
	// `reloadError`; a filter refused (400) goes to onQueryError as any page.
	// A press while one runs gets the same promise. Resolves with
	// `{ ok, removedIds }` (the ids on screen before that are not after), or
	// `{ ok: false }`; never rejects.
	const reload = useCallback(() => {
		if (reloadRef.current) return reloadRef.current;
		if (!enabledRef.current) return Promise.resolve({ ok: false });
		// Nothing of this feed has landed yet: the first page is still on its
		// way (nothing to do), or it failed (ask for it again, as Retry does).
		if (loadedPagesRef.current === 0) {
			if (failedPageRef.current !== null) retryRef.current();
			return Promise.resolve({ ok: false });
		}
		const pages = loadedPagesRef.current;
		const generation = ++generationRef.current;
		cancelWait(retryWaitRef);
		busyRef.current = true;
		setLoadingMore(false);
		setReloading(true);
		setReloadError(null);
		const queryNow = queryRef.current;

		const readFrom = async (pageIndex, rows, firstHeaders) => {
			const { data, headers } = await api.get(API_ITEMS_URL, { params: paramsForRef.current(pageIndex) });
			if (generation !== generationRef.current) return null;
			const pageRows = Array.isArray(data) ? data : [];
			const all = [...rows, ...pageRows];
			const first = pageIndex === 0 ? headers : firstHeaders;
			if (pageRows.length < PAGE_SIZE || pageIndex + 1 >= pages) {
				return { rows: all, firstHeaders: first, lastHeaders: headers, pagesRead: pageIndex + 1, lastFull: pageRows.length === PAGE_SIZE };
			}
			return readFrom(pageIndex + 1, all, first);
		};

		const run = readFrom(0, [], null).then((read) => {
			if (!read || generation !== generationRef.current) return { ok: false };
			// Two pages read a moment apart can both hold a record that moved
			// between them: it is listed once.
			const seen = new Set();
			const next = read.rows.filter((item) => {
				const id = `${item?._id?.$oid || item?._id || ''}`;
				if (!id) return true;
				if (seen.has(id)) return false;
				seen.add(id);
				return true;
			});
			const nextIds = new Set(next.map(item => `${item?._id?.$oid || item?._id || ''}`));
			const removedIds = (itemsRef.current || [])
				.map(item => `${item?._id?.$oid || item?._id || ''}`)
				.filter(id => id && !nextIds.has(id));
			const total = readTotalCount(read.lastHeaders, next.length);
			const more = read.lastFull && next.length < total;
			const report = readQueryWarnings(read.firstHeaders);
			setQueryReport(prev => (queryWarningsKey(prev) === queryWarningsKey(report) ? prev : report));
			const text = readCanonicalQuery(read.firstHeaders);
			setCanonical(prev => (prev && prev.query === queryNow && prev.text === text ? prev : { query: queryNow, text }));
			setItems(next);
			setTotalCount(total);
			hasMoreRef.current = more;
			setHasMore(more);
			loadedPagesRef.current = read.pagesRead;
			failedPageRef.current = null;
			setLoadError(null);
			return { ok: true, removedIds };
		}).catch((error) => {
			if (generation !== generationRef.current) return { ok: false };
			if (queryNow && error?.response?.status === 400 && onQueryErrorRef.current) {
				onQueryErrorRef.current(error.response?.data?.message, error.response?.data || {});
				return { ok: false };
			}
			setReloadError({ kind: error?.response?.status === 429 ? 'rate_limited' : 'failed' });
			return { ok: false };
		}).then((result) => {
			if (generation === generationRef.current) {
				reloadRef.current = null;
				setReloading(false);
				settle(generation, result.ok);
			}
			return result;
		});
		reloadRef.current = run;
		return run;
	}, [settle]);

	// An item deleted from the list (the card's or pane's Delete action) drops
	// out of the feed on the spot — no refetch, no scroll reset.
	const removeItem = useCallback((itemId) => {
		const id = `${itemId}`;
		setItems(prev => {
			const next = prev.filter(item => `${item?._id?.$oid || item?._id}` !== id);
			if (next.length !== prev.length) setTotalCount(count => Math.max(0, count - 1));
			return next;
		});
	}, []);

	// An item edited in place (the table's cell edit) takes its settled
	// document's place in the feed — same position, no refetch. An id the feed
	// does not hold is ignored: a row that paged out is not re-added.
	const replaceItem = useCallback((updated) => {
		const id = `${updated?._id?.$oid || updated?._id || ''}`;
		if (!id) return;
		setItems(prev => {
			const index = prev.findIndex(item => `${item?._id?.$oid || item?._id}` === id);
			if (index === -1) return prev;
			const next = [...prev];
			next[index] = updated;
			return next;
		});
	}, []);

	return {
		items,
		totalCount,
		totalKey,
		loading,
		loadingMore,
		hasMore,
		loadError,
		landedPages,
		queryWarnings: queryReport.warnings,
		queryWarningsOmitted: queryReport.omitted,
		canonicalQuery: canonical && canonical.query === query ? canonical.text : null,
		loadMore,
		retry,
		refresh,
		reload,
		reloading,
		reloadError,
		removeItem,
		replaceItem,
	};
}
